import React, { useState } from 'react';
import * as THREE from 'three';
import jsPDF from 'jspdf';
import { IconInstagram, IconFacebook, IconTwitter, IconTikTok, IconForm, IconDesign, IconReveal } from './Icons';
import { supbaseManager, getClient } from '../API';

/* ─── Hero ───────────────────────────────────────────────────────────────── */
export function Hero({ config }) {
  const { hero } = config;
  return (
    <section className="jb-hero" style={{ background: hero.bgFallback || '#111' }}>
      <div className="jb-hero__bg">
        {hero.bgImage && <img src={hero.bgImage} alt="hero" loading="eager" />}
      </div>
      <div className="jb-hero__content">
        <h1 className="jb-hero__title" style={{ fontSize: `${hero.fontSize || 86}px` }}>
          {hero.titleLine1}<br />{hero.titleLine2}
        </h1>
        <p className="jb-hero__sub">{hero.subtitle}</p>
        <a className="jb-cta" id="landing-iframe">{hero.ctaText}</a>{/**href={hero.ctaLink} */}
      </div>
    </section>
  );
}

/* ─── Logos Marquee ──────────────────────────────────────────────────────── */
export function LogosMarquee({ config }) {
  const logos = config?.logos?.items ?? [];
  const doubled = [...logos, ...logos];
  return (
    <section className="jb-logos">
      <div className="jb-logos__wrap">
        <div className="jb-logos__track">
          {doubled.map((logo, i) => (
            <div className="jb-logos__slot" key={i}>
              <img src={logo.url} alt={logo.name} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Boring Templates ───────────────────────────────────────────────────── */
export function BoringSection({ config }) {
  const { boring } = config;
  if (!boring.visible) return null;
  return (
    <section className="jb-boring">
      <h2 className="jb-boring__title" dangerouslySetInnerHTML={{ __html: boring.title.replace(/\n/g, '<br/>') }} />
      <p className="jb-boring__sub">{boring.subtitle}</p>
      <a className="jb-cta" href={boring.ctaLink}>{boring.ctaText}</a>
    </section>
  );
}

/* ─── Portfolio ──────────────────────────────────────────────────────────── */
export function PortfolioSection({ config }) {
  const { portfolio } = config;
  const [expanded, setExpanded] = React.useState(null);
  if (!portfolio.visible) return null;

  return (
    <section className="jb-portfolio">
      <h2 className="jb-portfolio__title">{portfolio.title}</h2>
      <div className="jb-portfolio__grid">
        {portfolio.items.map((item, i) => (
          <div className="jb-portfolio__card" key={i} onClick={() => setExpanded(item)}>
            <img src={item.img} alt={item.name} loading="lazy" />
            <div className="jb-portfolio__overlay">
              <span className="jb-portfolio__name">{item.name}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="jb-portfolio__cta">
        <a className="jb-cta" href={portfolio.ctaLink || '#'}>{portfolio.ctaText || 'COMEÇAR AGORA'}</a>
      </div>

      {expanded && (
        <div className="jb-portfolio__lightbox" onClick={() => setExpanded(null)}>
          <button className="jb-portfolio__lightbox-close" onClick={() => setExpanded(null)}>✕</button>
          <div className="jb-portfolio__lightbox-inner" onClick={e => e.stopPropagation()}>
            <img src={expanded.img} alt={expanded.name} className="jb-portfolio__lightbox-img" />
            {expanded.name && <span className="jb-portfolio__lightbox-name">{expanded.name}</span>}
          </div>
        </div>
      )}
    </section>
  );
}

/* ─── Reviews ────────────────────────────────────────────────────────────── */
export function ReviewsSection({ config }) {
  const { reviews } = config;
  if (!reviews.visible) return null;
  return (
    <section className="jb-reviews">
      <h2 className="jb-reviews__title" dangerouslySetInnerHTML={{ __html: reviews.title.replace(/\n/g, '<br/>') }} />
      <p className="jb-reviews__sub">{reviews.subtitle}</p>
      <div className="jb-reviews__stars">★★★★★</div>
      <div className="jb-reviews__badge">
        <span style={{ fontFamily: 'sans-serif', fontSize: 22, fontWeight: 900, background: 'linear-gradient(135deg,#4285f4,#ea4335,#fbbc05,#34a853)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>G</span>
        <div>
          <div className="jb-reviews__badge-label">Avaliação Google</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="jb-reviews__badge-score">{reviews.googleScore}</span>
            <span style={{ color: '#f5a623', fontSize: 13 }}>★★★★★</span>
            <span className="jb-reviews__badge-sub">{reviews.reviewCount} avaliações</span>
          </div>
        </div>
      </div>
      <div className="jb-reviews__grid">
        {reviews.items.map((r, i) => (
          <div className="jb-review-card" key={i}>
            <div className="jb-review-card__head">
              <div className="jb-review-card__avatar" style={{ background: r.color }}>{r.name.charAt(0)}</div>
              <div>
                <div className="jb-review-card__name">{r.name}</div>
                <div className="jb-review-card__time">{r.time}</div>
              </div>
            </div>
            <div className="jb-review-card__stars">★★★★★</div>
            <p className="jb-review-card__text">{r.text}</p>
            <div className="jb-review-card__src">
              <div className="jb-review-card__g-dot">G</div>
              <span>Publicado no Google</span>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 36 }}>
        <a className="jb-cta" href="https://jerseybird.com/pages/paperform">COMEÇAR AGORA</a>
      </div>
    </section>
  );
}

/* ─── How It Works ───────────────────────────────────────────────────────── */
const HOW_ICONS = [<IconForm />, <IconDesign />, <IconReveal />];

export function HowItWorksSection({ config }) {
  const { howItWorks } = config;
  if (!howItWorks.visible) return null;
  return (
    <section className="jb-how">
      <h2 className="jb-how__title">{howItWorks.title}</h2>
      <div className="jb-how__grid">
        {howItWorks.steps.map((step, i) => (
          <div className="jb-how__step" key={i}>
            <div className="jb-how__icon">
              {step.image
                ? <img src={step.image} alt={step.title} className="jb-how__icon-img" />
                : (HOW_ICONS[i] ?? <span style={{ fontSize: 42 }}>{step.icon}</span>)
              }
            </div>
            <h3 className="jb-how__step-title">{step.title}</h3>
            <p className="jb-how__step-desc">{step.description}</p>
          </div>
        ))}
      </div>
      <a className="jb-cta" href="https://jerseybird.com/pages/paperform">COMEÇAR AGORA</a>
    </section>
  );
}


/* ─── Testimonials ───────────────────────────────────────────────────────── */
export function TestimonialsSection({ config }) {
  const { testimonials } = config;
  const [active, setActive] = useState(0);  // ← ANTES do if

  if (!testimonials?.visible) return null;

  const items = testimonials.items ?? [];
  if (items.length === 0) return null;

  const prev = () => setActive((i) => (i - 1 + items.length) % items.length);
  const next = () => setActive((i) => (i + 1) % items.length);
  const item = items[active];
  // ... resto igual
  
  return (
    <section className="jb-testimonials">
      <h2 className="jb-testimonials__title">{testimonials.title}</h2>

      <div className="jb-testimonials__carousel">
        <button className="jb-testimonials__arrow jb-testimonials__arrow--prev" onClick={prev}>&#8592;</button>

        <div className="jb-testimonials__card">
          {item.thumb && (
            <img className="jb-testimonials__thumb" src={item.thumb} alt={item.name} />
          )}
          {!item.thumb && (
            <div className="jb-testimonials__thumb jb-testimonials__thumb--fallback">
              {item.name?.charAt(0) ?? '?'}
            </div>
          )}
          <p className="jb-testimonials__text">"{item.text}"</p>
          <span className="jb-testimonials__name">{item.name}</span>
          {item.role && <span className="jb-testimonials__role">{item.role}</span>}
        </div>

        <button className="jb-testimonials__arrow jb-testimonials__arrow--next" onClick={next}>&#8594;</button>
      </div>

      <div className="jb-testimonials__dots">
        {items.map((_, i) => (
          <button
            key={i}
            className={`jb-testimonials__dot${i === active ? ' jb-testimonials__dot--active' : ''}`}
            onClick={() => setActive(i)}
          />
        ))}
      </div>
    </section>
  );
}



/* ─── Categories ─────────────────────────────────────────────────────────── */
const SUPABASE_THUMBS = 'https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/svgs-thumbs/';

function useCategories() {
  const [categories, setCategories] = React.useState([]);

  React.useEffect(() => {
    // Tenta pegar categorias já carregadas (formato já processado pelo categories.tsx)
    if (window.categoriesLoaded && window.categoriesLoaded.length > 0) {
      setCategories(window.categoriesLoaded);
    }

    // Intercepta chamadas futuras ao loadCategories para detectar novos carregamentos
    const originalLoad = window.loadCategories;
    window.loadCategories = function(cats) {
      if (originalLoad) originalLoad(cats);
      // window.categoriesLoaded é setado dentro do loadCategories original
      // aguarda um tick para pegar o valor atualizado
      setTimeout(() => {
        if (window.categoriesLoaded && window.categoriesLoaded.length > 0) {
          setCategories([...window.categoriesLoaded]);
        }
      }, 150);
    };

    // Polling como fallback — cobre casos onde categorias chegam depois da montagem
    const interval = setInterval(() => {
      if (window.categoriesLoaded && window.categoriesLoaded.length > 0) {
        setCategories(prev => {
          if (prev.length !== window.categoriesLoaded.length) {
            return [...window.categoriesLoaded];
          }
          return prev;
        });
      }
    }, 500);

    return () => {
      clearInterval(interval);
      if (originalLoad) window.loadCategories = originalLoad;
      else delete window.loadCategories;
    };
  }, []);

  return categories;
}


const SUPABASE_TECIDOS = 'https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/tecidos/';
const SUPABASE_ESCUDOS = 'https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/escudos/';

function parseFormItems(jsonStr, baseUrl) {
  try {
    const arr = JSON.parse(jsonStr || '[]');
    return arr.map(item => {
      const key = Object.keys(item)[0];
      const path = item[key];
      return { id: key, name: key.replace(/\d+$/, ''), img: baseUrl + path };
    });
  } catch { return []; }
}

/**
 * useModels — busca modelos da tabela 3ds para o dono da landing.
 * Usa window.__formModels se já populado pelo Loads.js,
 * senão busca direto do Supabase filtrando por form === true.
 * O design.sport_id é a URL do .glb — filtra o modelo cujo path
 * bate com essa URL para mostrar tecidos/escudos do modelo correto.
 */
export function useModels(design, ownerId) {
  const [models, setModels] = React.useState([]);

  React.useEffect(() => {
    // 1. Se já populado pelo Loads.js, usa direto
    if (window.__formModels && window.__formModels.length > 0) {
      setModels(window.__formModels);
      return;
    }

    // 2. Busca do Supabase — usa o ownerId explícito quando informado (caso do
    // simulador embedado, onde quem está vendo não é necessariamente o dono
    // do catálogo); senão cai no comportamento antigo (landing page, onde o
    // dono sempre é o usuário logado no próprio localStorage).
    const userId = ownerId || localStorage.getItem('user_id');
    if (!userId) return;

    supbaseManager('get', '3ds', null, userId)
      .then(data => {
        const filtered = (data || [])
          .filter(item => item.form === true)
          .map(item => {
            let thumb = 'https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/images/' + item.thumb2;
            if (item.thumb2?.includes('/models/novos/')) thumb = item.thumb2;
            return {
              id: item.id,
              name: item.name,
              path: item.path,
              thumb,
              price: parseFloat(item.price) || 0,
              sizes: item.sizes || '',
              collar: item.collar || '',
              sleeve: item.sleeve || '',
              fabrics: parseFormItems(item.tecidos, SUPABASE_TECIDOS),
              shields: parseFormItems(item.escudos, SUPABASE_ESCUDOS),
            };
          });
        window.__formModels = filtered;
        setModels(filtered);
      })
      .catch(e => console.error('[useModels]', e));

    // 3. Escuta window.setFormModels (caso Loads.js chame depois)
    const prev = window['setFormModels'];
    window['setFormModels'] = (list) => {
      if (prev) prev(list);
      const mapped = list.map(m => ({
        ...m,
        fabrics: m.fabrics ?? [],
        shields: m.shields ?? [],
      }));
      window.__formModels = mapped;
      setModels(mapped);
    };
    return () => { if (prev) window['setFormModels'] = prev; };
  }, []);

  // Tenta encontrar o modelo que corresponde ao design pelo path do .glb
  const matchedModel = React.useMemo(() => {
    if (!design?.sport_id || models.length === 0) return null;
    // sport_id é a URL completa do .glb: .../3ds/userId/path.glb
    // path do modelo é só "userId/path" sem extensão
    return models.find(m => design.sport_id?.includes(m.path)) ?? null;
  }, [models, design?.sport_id]);

  return { models, matchedModel };
}


/* ─── Order Summary + PDF ────────────────────────────────────────────────── */
function toPtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

// Tira um print bem enquadrado do modelo 3D — em vez de só girar e cortar
// de um canvas largo (que sobra muita área morta nas laterais), calcula a
// bounding box do modelo, redimensiona o canvas pra retrato (3:4) e reposiciona
// a câmera pra caber o modelo com uma margem pequena. Devolve tudo ao estado
// original no final (canvas, câmera, rotação do modelo).
async function captureTightShot({ rotateY = 0 } = {}) {
  if (typeof window === 'undefined') return null;
  const { model, renderer, camera, scene, controls } = window;
  if (!model || !renderer || !camera || !scene) return null;

  const originalRotationY = model.rotation.y;
  const originalWidth = renderer.domElement.width;
  const originalHeight = renderer.domElement.height;
  const originalAspect = camera.aspect;
  const originalPixelRatio = renderer.getPixelRatio ? renderer.getPixelRatio() : 1;
  const originalCameraPos = camera.position.clone();
  const originalTarget = controls ? controls.target.clone() : null;

  try {
    model.rotation.y = originalRotationY + rotateY;
    model.updateWorldMatrix(true, true);

    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    // Canvas retrato (3:4) — sem sobra lateral de um canvas widescreen.
    const targetW = 900, targetH = 1200;
    renderer.setPixelRatio(1);
    renderer.setSize(targetW, targetH, false);
    camera.aspect = targetW / targetH;

    // Distância mínima pra caber altura E largura do modelo, com ~15% de folga.
    const fov = camera.fov * (Math.PI / 180);
    const distanceForHeight = (size.y / 2) / Math.tan(fov / 2);
    const distanceForWidth = (size.x / 2) / Math.tan((fov * camera.aspect) / 2);
    const distance = Math.max(distanceForHeight, distanceForWidth) * 1.15;

    camera.position.set(center.x, center.y, center.z + distance);
    camera.lookAt(center);
    camera.updateProjectionMatrix();

    renderer.render(scene, camera);
    await new Promise((resolve) => setTimeout(resolve, 50));
    renderer.render(scene, camera);

    return renderer.domElement.toDataURL('image/png');
  } catch (e) {
    console.warn('[captureTightShot] erro ao capturar imagem enquadrada:', e);
    return null;
  } finally {
    model.rotation.y = originalRotationY;
    renderer.setPixelRatio(originalPixelRatio);
    renderer.setSize(originalWidth, originalHeight, false);
    camera.aspect = originalAspect;
    camera.position.copy(originalCameraPos);
    if (controls && originalTarget) controls.target.copy(originalTarget);
    camera.updateProjectionMatrix();
    if (controls) controls.update();
    renderer.render(scene, camera); // devolve a cena visível ao estado normal
  }
}

export function OrderSummary({ result, config, onClose }) {
  const logoUrl = config?.nav?.logoImage || '';
  const brandRaw = config?.brand;
  const brandName = typeof brandRaw === 'object' ? (brandRaw?.name || 'Resumo de Pedido') : (brandRaw || 'Resumo de Pedido');
  const r = result;
  const o = r.order;
  const d = r.design;
  const c = r.contact;

  // Carrega uma imagem (URL remota ou já em data:) como data URL, pra poder
  // ser inserida no PDF. Se falhar (ex: CORS bloqueando a imagem), retorna
  // null e o PDF é gerado sem a imagem em vez de quebrar tudo.
  const toDataURL = async (url) => {
    if (!url) return null;
    if (url.startsWith('data:')) return url;
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('[OrderSummary] não foi possível carregar imagem pro PDF:', e);
      return null;
    }
  };

  const generatePDF = async () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    let y = 54;

    const ensureSpace = (needed) => {
      if (y + needed > pageHeight - margin) {
        doc.addPage();
        y = 54;
      }
    };

    // Header
    const logoData = await toDataURL(logoUrl);
    if (logoData) {
      try {
        const logoBoxW = 90, logoBoxH = 30;
        const props = doc.getImageProperties(logoData);
        const ratio = Math.min(logoBoxW / props.width, logoBoxH / props.height);
        const logoW = props.width * ratio;
        const logoH = props.height * ratio;
        const format = logoData.includes('image/png') ? 'PNG' : 'JPEG';
        doc.addImage(logoData, format, margin, y - logoH + 4, logoW, logoH);
      } catch (e) {
        console.warn('[OrderSummary] falha ao inserir logo no PDF:', e);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.setTextColor('#e8272a');
        doc.text(brandName, margin, y);
      }
    } else {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor('#e8272a');
      doc.text(brandName, margin, y);
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor('#888888');
    doc.text(`Gerado em ${toPtDate(r.submittedAt)}`, pageWidth - margin, y, { align: 'right' });
    y += 12;
    doc.setDrawColor('#bbbbbb');
    doc.setLineWidth(1);
    doc.line(margin, y, pageWidth - margin, y);
    y += 30;

    // Nome do design — centralizado, acima das imagens.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor('#111111');
    doc.text(d.name || '', pageWidth / 2, y, { align: 'center' });
    y += 24;

    // Design (imagem) — frente e costas lado a lado, bem grandes, centralizadas
    // na página. Quando o modelo 3D ainda está na tela, giramos e tiramos um
    // print da parte de trás na hora; se não tiver o modelo ao vivo (ex: PDF
    // gerado a partir de um design salvo, sem simulador aberto), usa só a
    // imagem que já tínhamos (frente) e centraliza sozinha.
    const boxW = 245, boxH = 320;
    const gap = 20;

    const drawBoxedImage = async (imgData, x) => {
      if (!imgData) return false;
      try {
        const props = doc.getImageProperties(imgData);
        const ratio = Math.min(boxW / props.width, boxH / props.height);
        const w = props.width * ratio, h = props.height * ratio;
        const offsetX = (boxW - w) / 2;
        const offsetY = (boxH - h) / 2;
        const format = imgData.includes('image/png') ? 'PNG' : 'JPEG';
        doc.addImage(imgData, format, x + offsetX, y + offsetY, w, h);
        return true;
      } catch (e) {
        console.warn('[OrderSummary] falha ao inserir imagem no PDF:', e);
        return false;
      }
    };

    // Tenta capturar frente e costas bem enquadradas (canvas redimensionado
    // pra caber o modelo, sem área morta) direto da cena 3D ao vivo. Se não
    // tiver o simulador na tela (ex: PDF gerado a partir de um design salvo,
    // sem canvas 3D ativo), cai pra thumbnail estática que já tínhamos (só frente).
    const liveSceneAvailable = typeof window !== 'undefined' && window.model && window.renderer && window.camera;

    let frontData = null;
    let backData = null;

    if (liveSceneAvailable) {
      frontData = await captureTightShot({ rotateY: 0 });
      backData = await captureTightShot({ rotateY: Math.PI });
    } else {
      frontData = await toDataURL(d.thumbnail);
    }

    const totalWidth = backData ? boxW * 2 + gap : boxW;
    const startX = (pageWidth - totalWidth) / 2;

    const frontOk = await drawBoxedImage(frontData, startX);
    if (frontOk) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor('#999999');
      doc.text('FRENTE', startX + boxW / 2, y + boxH + 16, { align: 'center' });
    }

    if (backData) {
      const backOk = await drawBoxedImage(backData, startX + boxW + gap);
      if (backOk) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor('#999999');
        doc.text('COSTAS', startX + boxW + gap + boxW / 2, y + boxH + 16, { align: 'center' });
      }
    }

    y += boxH + 40;

    const sectionTitle = (title) => {
      ensureSpace(24);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor('#e8272a');
      doc.text(title.toUpperCase(), margin, y);
      y += 18;
    };

    const fieldLine = (label, value) => {
      if (!value) return;
      const lines = doc.splitTextToSize(String(value), pageWidth - margin * 2);
      ensureSpace(14 + lines.length * 13 + 8);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor('#999999');
      doc.text(label.toUpperCase(), margin, y);
      y += 12;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor('#111111');
      doc.text(lines, margin, y);
      y += lines.length * 13 + 10;
    };

    sectionTitle('Detalhes do Pedido');
    fieldLine('Objetivos', o.purposes?.join(', '));
    fieldLine('Data de Entrega', o.date ? toPtDate(o.date) : '');
    fieldLine('Lista de Pedidos', o.orderList);
    fieldLine('Descrição', o.description);

    y += 6;
    sectionTitle('Contato');
    fieldLine('Nome', c.firstName);
    fieldLine('E-mail', c.email);
    fieldLine('Telefone', c.phone ? `${c.ddi} ${c.phone}` : '');
    fieldLine('Endereço', c.address);

    const filename = `pedido-${(d.name || 'orcamento').toString().trim().replace(/\s+/g, '-')}.pdf`;
    doc.save(filename);
  };

  return (
    <div className="jb-form__success">
      <div className="jb-form__success-icon">✅</div>
      <h3 className="jb-form__success-title">Pedido registrado!</h3>
      <p className="jb-form__success-sub">Entraremos em contato em breve.</p>
      {r.orderId != null && (
        <p style={{ fontSize: 13, color: '#888', marginTop: 4 }}>
          Referência do pedido: <strong>#{r.orderId}</strong>
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', marginTop: 8 }}>
        {r.whatsappUrl && (
          <button
            className="jb-form__btn jb-form__btn--primary"
            style={{ background: '#25D366', borderColor: '#25D366' }}
            onClick={() => window.open(r.whatsappUrl, '_blank')}
          >
            📲 Enviar por WhatsApp
          </button>
        )}
        <button className="jb-form__btn jb-form__btn--primary" onClick={() => generatePDF()}>
          📄 Baixar PDF
        </button>
        <button
          className="jb-form__btn"
          style={{ background: '#f2f2f2', color: '#333', border: '1px solid #ddd' }}
          onClick={onClose}
        >Fechar</button>
      </div>
    </div>
  );
}

/* ─── Order Form ─────────────────────────────────────────────────────────── */
export const PURPOSES = ['Terceirão','Formandos','Evento','Campeonato','Time','Empresa','Interclasse','Carnaval','Corrida','Pesca','Agro','Outro'];

export function OrderForm({ design, onClose, categoryProjects = [], config = {}, ownerId, onSubmit }) {
  const [step, setStep] = React.useState(1);
  const TOTAL_STEPS = 4;

  const [form, setForm] = React.useState({
    firstName: '', address: '',
    phoneType: 'Celular', email: '',
    ddi: '+55', phone: '',
    purposes: [],
    date: '',
    description: '',
    orderList: '',
    logos: [],
    result: null,
  });

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const togglePurpose = (p) => setForm(prev => ({
    ...prev,
    purposes: prev.purposes.includes(p)
      ? prev.purposes.filter(x => x !== p)
      : [...prev.purposes, p]
  }));

  const handleLogoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    Promise.all(files.map(f => new Promise(res => {
      const r = new FileReader();
      r.onload = () => res({ name: f.name, data: r.result });
      r.readAsDataURL(f);
    }))).then(imgs => setForm(p => ({ ...p, logos: [...p.logos, ...imgs].slice(0, 4) })));
  };

  const [submitting, setSubmitting] = React.useState(false);

  const finish = async () => {
    const activeDesign = form.selectedDesign ?? design;

    const result = {
      design: {
        id: activeDesign.id_data ?? activeDesign.id,
        name: activeDesign.name,
        thumbnail: activeDesign.thumbnail,
      },
      contact: {
        firstName: form.firstName, address: form.address,
        email: form.email, phoneType: form.phoneType,
        ddi: form.ddi, phone: form.phone,
      },
      order: {
        purposes: form.purposes,
        date: form.date,
        description: form.description,
        orderList: form.orderList,
      },
      logos: form.logos.map(l => l.name),
      submittedAt: new Date().toISOString(),
    };
    // Agora espera o retorno do onSubmit (ex: gravar em iframe_projects) pra
    // já ter o id de referência e o link do WhatsApp prontos na tela de
    // sucesso — por isso o WhatsApp não abre mais sozinho aqui, vira um
    // botão que o próprio cliente clica (evita depender de timing/popup).
    if (onSubmit) {
      setSubmitting(true);
      try {
        const extra = await onSubmit(result);
        if (extra?.orderId != null) result.orderId = extra.orderId;
        if (extra?.whatsappUrl) result.whatsappUrl = extra.whatsappUrl;
      } catch (e) {
        console.error('[OrderForm] erro no onSubmit:', e);
      } finally {
        setSubmitting(false);
      }
    }

    upd('result', result);
    setStep(TOTAL_STEPS + 1);
  };

  if (form.result) {
    return (
      <OrderSummary
        result={form.result}
        config={config}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="jb-form">
      {/* Progress */}
      <div className="jb-form__progress">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div key={i} className={`jb-form__progress-dot ${step > i ? 'jb-form__progress-dot--done' : ''} ${step === i + 1 ? 'jb-form__progress-dot--active' : ''}`} />
        ))}
      </div>

      {/* Step 1 — Contato */}
      {step === 1 && (
        <div className="jb-form__step">
          <h3 className="jb-form__step-title">Seus dados</h3>
          <p className="jb-form__step-sub"><strong>Envie seus dados</strong> que entraremos em contato em breve<br/><small style={{color:'#aaa'}}>*Todos os campos são obrigatórios</small></p>
          <div className="jb-form__field"><label>Nome</label><input value={form.firstName} onChange={e => upd('firstName', e.target.value)} placeholder="Nome" /></div>
          <div className="jb-form__field"><label>E-mail</label><input type="email" value={form.email} onChange={e => upd('email', e.target.value)} placeholder="seu@email.com" /></div>
          <div className="jb-form__field"><label>Endereço</label><input value={form.address} onChange={e => upd('address', e.target.value)} placeholder="Rua, número, bairro, cidade" /></div>
          <div className="jb-form__field">
            <label>DDD + Telefone</label>
            <div className="jb-form__phone-row">
              <input value={form.ddi} onChange={e => upd('ddi', e.target.value)} className="jb-form__ddi" placeholder="+55" />
              <input value={form.phone} onChange={e => upd('phone', e.target.value)} placeholder="(00) 00000-0000" style={{ flex: 1 }} />
            </div>
          </div>
        </div>
      )}

      {/* Step 2 — Finalidade + Data */}
      {step === 2 && (
        <div className="jb-form__step">
          <h3 className="jb-form__step-title">Para o que você precisa?</h3>
          <div className="jb-form__chips">
            {PURPOSES.map(p => (
              <button key={p} className={`jb-form__chip ${form.purposes.includes(p) ? 'jb-form__chip--active' : ''}`} onClick={() => togglePurpose(p)}>{p}</button>
            ))}
          </div>
          <h3 className="jb-form__step-title" style={{ marginTop: 24 }}>Qual data você precisa?</h3>
          <input type="date" className="jb-form__date" value={form.date} onChange={e => upd('date', e.target.value)} />
        </div>
      )}

      {/* Step 3 — Lista de Pedidos + Descrição + Logos */}
      {step === 3 && (
        <div className="jb-form__step">
          <h3 className="jb-form__step-title">Lista de pedidos</h3>
          <textarea className="jb-form__textarea" value={form.orderList} onChange={e => upd('orderList', e.target.value)} placeholder="Ex: 10 camisas M, 5 calções G, 3 meiões..." rows={4} />
          <h3 className="jb-form__step-title" style={{ marginTop: 20 }}>Descrição adicional</h3>
          <textarea className="jb-form__textarea" value={form.description} onChange={e => upd('description', e.target.value)} placeholder="Descreva aqui detalhes do seu pedido" rows={4} />
          <h3 className="jb-form__step-title" style={{ marginTop: 20 }}>Anexo logos</h3>
          <label className="jb-form__upload-btn">
            📎 Adicionar arquivo
            <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleLogoUpload} />
          </label>
          <div className="jb-form__logo-previews">
            {form.logos.map((l, i) => (
              <div key={i} className="jb-form__logo-preview">
                <img src={l.data} alt={l.name} />
                <button onClick={() => setForm(p => ({ ...p, logos: p.logos.filter((_, idx) => idx !== i) }))}>✕</button>
              </div>
            ))}
            {Array.from({ length: Math.max(0, 4 - form.logos.length) }).map((_, i) => (
              <div key={'empty-' + i} className="jb-form__logo-empty" />
            ))}
          </div>
        </div>
      )}

      {/* Step 4 — Resumo */}
      {step === 4 && (
        <div className="jb-form__step">
          <h3 className="jb-form__step-title">Resumo final</h3>
          <div className="jb-form__summary">
            <div className="jb-form__summary-row"><span>Design</span><strong>{design.name}</strong></div>
            <div className="jb-form__summary-row"><span>Nome</span><strong>{form.firstName}</strong></div>
            <div className="jb-form__summary-row"><span>E-mail</span><strong>{form.email}</strong></div>
            <div className="jb-form__summary-row"><span>Endereço</span><strong>{form.address}</strong></div>
            <div className="jb-form__summary-row"><span>Telefone</span><strong>{form.ddi} {form.phone}</strong></div>
            {form.purposes.length > 0 && <div className="jb-form__summary-row"><span>Finalidade</span><strong>{form.purposes.join(', ')}</strong></div>}
            {form.date && <div className="jb-form__summary-row"><span>Data</span><strong>{form.date}</strong></div>}
            {form.orderList && <div className="jb-form__summary-row"><span>Lista de pedidos</span><strong>{form.orderList}</strong></div>}
            {form.description && <div className="jb-form__summary-row"><span>Descrição</span><strong>{form.description}</strong></div>}
            {form.logos.length > 0 && <div className="jb-form__summary-row"><span>Logos</span><strong>{form.logos.length} arquivo(s)</strong></div>}
          </div>
        </div>
      )}

      {/* Navigation */}
      {(() => {
        const validate = () => {
          if (step === 1) {
            if (!form.firstName.trim()) { alert('Por favor, informe o seu nome.'); return false; }
            if (!form.email.trim() || !form.email.includes('@')) { alert('Por favor, informe um e-mail válido.'); return false; }
            if (!form.phone.trim()) { alert('Por favor, informe o telefone.'); return false; }
          }
          if (step === 2) {
            if (form.purposes.length === 0) { alert('Selecione pelo menos um objetivo.'); return false; }
            if (!form.date) { alert('Por favor, informe a data de entrega.'); return false; }
          }
          return true;
        };
        return (
          <div className="jb-form__nav">
            {step > 1
              ? <button
                  className="jb-form__btn jb-form__btn--back"
                  style={{ background: '#f2f2f2', color: '#333', border: '1px solid #ddd' }}
                  onClick={() => setStep(s => s - 1)}
                >← Voltar</button>
              : <div />
            }
            {step < TOTAL_STEPS
              ? <button className="jb-form__btn jb-form__btn--primary" onClick={() => { if (validate()) setStep(s => s + 1); }}>Próximo →</button>
              : <button
                  className="jb-form__btn jb-form__btn--primary"
                  disabled={submitting}
                  onClick={() => { if (validate()) finish(); }}
                >{submitting ? "Enviando..." : "✓ Enviar pedido"}</button>
            }
          </div>
        );
      })()}
    </div>
  );
}

export function DesignModal({ design, onClose, config, categoryProjects = [], ownerId }) {
  const [mode, setMode] = React.useState(null); // null | 'viewer' | 'customize' | 'form'
  const [iframeLoading, setIframeLoading] = React.useState(true);
  if (!design) return null;

  const designId = design.id_data ?? design.id;
  const URLS = {
    viewer:    `https://simuladorksports.com?i=${designId}&d=1`,
    customize: `https://simuladorksports.com?i=${designId}&edit=1`,
  };

  const openMode = (m) => { setIframeLoading(true); setMode(m); };

  // O dono da loja: prop explícita > setado pela JerseyBirdPage em modo
  // catálogo (window.jbOwnerId) > usuário logado no próprio localStorage
  // (quando é o dono navegando/pré-visualizando o próprio site).
  const resolvedOwnerId = ownerId || (typeof window !== 'undefined' && window.jbOwnerId) || localStorage.getItem('user_id');

  // Ao enviar o formulário "Tenho interesse", busca o WhatsApp da loja e
  // abre sozinho com a mensagem pronta assim que resolvido. O link também é
  // devolvido pro OrderForm — se o navegador bloquear essa abertura
  // automática, o botão de WhatsApp continua disponível na tela de sucesso.
  const handleFormSubmit = async (result) => {
    try {
      if (!resolvedOwnerId) return {};
      const client = await getClient(resolvedOwnerId);
      const whatsapp = client?.[0]?.whatsapp;
      if (!whatsapp) return {};

      const digits = String(whatsapp).replace(/\D/g, '');
      const phone = digits.startsWith('55') ? digits : `55${digits}`;

      const c = result.contact || {};
      const lines = [
        `🛍️ *Tenho interesse nesse modelo*`,
        `*Modelo:* ${result.design?.name || ''}`,
        `*Referência/ID:* #${result.design?.id ?? ''}`,
        `*Nome:* ${c.firstName || ''}`,
        c.phone ? `*Telefone:* ${c.ddi || ''} ${c.phone}` : null,
        c.email ? `*E-mail:* ${c.email}` : null,
        c.address ? `*Endereço:* ${c.address}` : null,
      ].filter(Boolean);

      const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(lines.join('\n'))}`;
      window.open(whatsappUrl, '_blank');

      return { whatsappUrl };
    } catch (e) {
      console.error('[DesignModal] erro ao montar link do WhatsApp:', e);
      return {};
    }
  };

  const isForm = mode === 'form';
  const isIframe = mode === 'viewer' || mode === 'customize';

  return (
    <div className="jb-modal-overlay">
      <div className={`jb-modal jb-modal--design-full ${isForm ? 'jb-modal--with-form' : ''}`}>
        <button className="jb-modal__close" onClick={mode ? () => setMode(null) : onClose}>
          {mode ? '← Voltar' : '✕'}
        </button>

        {/* Iframe modes */}
        {isIframe && (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {iframeLoading && (
              <div className="jb-iframe-loading">
                <div className="jb-iframe-spinner" />
                <span className="jb-iframe-loading-text">Carregando...</span>
              </div>
            )}
            <iframe
              src={URLS[mode]}
              className="jb-modal__viewer-iframe"
              title={mode}
              allowFullScreen
              onLoad={() => setIframeLoading(false)}
              style={{ opacity: iframeLoading ? 0 : 1, transition: 'opacity .4s' }}
            />
          </div>
        )}

        {/* Form mode — split layout */}
        {isForm && (
          <div className="jb-modal--split">
            <div className="jb-modal--split-left">
              <img src={design.thumbnail} alt={design.name} className="jb-modal__design-fullimg" style={{ maxHeight: '100%', objectFit: 'contain' }} />
            </div>
            <div className="jb-modal--split-right">
              <h2 className="jb-form__header-title">Tenho interesse</h2>
              <OrderForm
                design={design}
                onClose={() => setMode(null)}
                categoryProjects={categoryProjects}
                config={config}
                ownerId={resolvedOwnerId}
                onSubmit={handleFormSubmit}
              />
            </div>
          </div>
        )}

        {/* Default — thumbnail + 3 buttons */}
        {!mode && (
          <div className="jb-modal__design-fullscreen">
            <img src={design.thumbnail} alt={design.name} className="jb-modal__design-fullimg" />
            <div className="jb-modal__design-overlay">
              <span className="jb-modal__design-title">{design.name}</span>
              <div className="jb-modal__design-actions">
                <button className="jb-modal__action-btn jb-modal__action-btn--primary" onClick={() => openMode('viewer')}>🎽 Visualizar</button>
                <button className="jb-modal__action-btn" onClick={() => openMode('customize')}>✏️ Personalizar</button>
                <button className="jb-modal__action-btn" onClick={() => openMode('form')}>📋 Formulário</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryModal({ category, thumbnails, onClose, onSelectDesign }) {
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!category) return;
    const ids = (category.projectIds || []).map(Number).filter(Boolean);
    if (ids.length === 0) { setLoading(false); return; }

    setLoading(true);
    supbaseManager('getByids', 'simulator', ids)
      .then((data) => {
        const THUMB_BASE = 'https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/svgs-thumbs/';
        const parsed = (data || []).map(p => ({
          id:          p.id,
          id_data:     p.id,
          name:        p.name,
          thumbnail:   THUMB_BASE + p.svg_thumb_path,
          description: '',
          sport_id:    p.sport_id,
          svg_path:    p.svg_path,
          body_state:  p.body_state,
          up_state:    p.up_state,
          down_state:  p.down_state,
          socks_state: p.socks_state,
          clone_state: p.clone_state,
          images:      p.images,
          inside_color: p.inside_color,
          up_state2:   p.up_state2,
          collar:      p.collar,
        }));
        setProjects(parsed);
      })
      .catch(err => { console.error('[CategoryModal] fetch error:', err); })
      .finally(() => setLoading(false));
  }, [category]);

  if (!category) return null;

  return (
    <div className="jb-modal-overlay" onClick={onClose}>
      <div className="jb-modal jb-modal--category" onClick={e => e.stopPropagation()}>
        <button className="jb-modal__close" onClick={onClose}>✕</button>
        <h2 className="jb-modal__title">{category.name}</h2>
        <p className="jb-modal__count">{projects.length} design{projects.length !== 1 ? 's' : ''}</p>
        {loading ? (
          <div className="jb-modal__loading">Carregando...</div>
        ) : projects.length === 0 ? (
          <div className="jb-modal__empty">Nenhum design nesta categoria</div>
        ) : (
          <div className="jb-modal__grid">
            {projects.map((p, i) => (
              <div key={i} className="jb-modal__card" onClick={() => onSelectDesign(p)}>
                <img src={p.thumbnail} alt={p.name} className="jb-modal__card-img" />
                <span className="jb-modal__card-name">{p.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function CategoriesSection({ config }) {
  const { categories: catConfig } = config;
  const [activeCategory, setActiveCategory] = React.useState(null);
  const [activeDesign, setActiveDesign] = React.useState(null);
  const [categoryProjects, setCategoryProjects] = React.useState([]);
  const categories = useCategories();

  if (!catConfig?.visible) return null;

  const thumbnails = catConfig?.thumbnails ?? {};

  return (
    <section className="jb-categories">
      {catConfig.title && <h2 className="jb-categories__title">{catConfig.title}</h2>}
      {catConfig.subtitle && <p className="jb-categories__sub">{catConfig.subtitle}</p>}

      <div className="jb-categories__grid">
        {categories.map((cat, i) => {
          const thumb = thumbnails[String(cat.dbId)] || thumbnails[String(cat.id)] || null;
          return (
            <div key={i} className="jb-categories__card" onClick={() => setActiveCategory(cat)}>
              <div className="jb-categories__card-img-wrap">
                {thumb
                  ? <img src={thumb} alt={cat.name} className="jb-categories__card-img" />
                  : <div className="jb-categories__card-placeholder">🎽</div>
                }
                {cat.shared && <span className="jb-categories__badge">Compartilhado</span>}
              </div>
              <div className="jb-categories__card-body">
                <span className="jb-categories__card-name">{cat.name}</span>
                <span className="jb-categories__card-count">{(cat.projectIds || []).length} designs</span>
              </div>
            </div>
          );
        })}
      </div>

      {activeCategory && !activeDesign && (
        <CategoryModal
          category={activeCategory}
          thumbnails={thumbnails}
          onClose={() => setActiveCategory(null)}
          onSelectDesign={(design, projs) => { setActiveDesign(design); setCategoryProjects(projs || []); }}
        />
      )}

      {activeDesign && (
        <DesignModal
          design={activeDesign}
          onClose={() => setActiveDesign(null)}
          config={config}
          categoryProjects={categoryProjects}
        />
      )}
    </section>
  );
}

/* ─── Location Map ───────────────────────────────────────────────────────── */
export function LocationSection({ config }) {
  const { location } = config;
  if (!location?.visible) return null;
  if (!location?.embedUrl) return null;

  return (
    <section className="jb-location">
      {location.title && <h2 className="jb-location__title">{location.title}</h2>}
      {location.subtitle && <p className="jb-location__sub">{location.subtitle}</p>}
      <div className="jb-location__card">
        <iframe
          className="jb-location__map"
          src={location.embedUrl}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Localização"
        />
        {(location.address || location.phone || location.hours) && (
          <div className="jb-location__info">
            {location.address && (
              <div className="jb-location__info-row">
                <span className="jb-location__info-icon">📍</span>
                <span>{location.address}</span>
              </div>
            )}
            {location.phone && (
              <div className="jb-location__info-row">
                <span className="jb-location__info-icon">📞</span>
                <span>{location.phone}</span>
              </div>
            )}
            {location.hours && (
              <div className="jb-location__info-row">
                <span className="jb-location__info-icon">🕐</span>
                <span>{location.hours}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── Footer ─────────────────────────────────────────────────────────────── */
export function Footer({ config }) {
  const brandRaw = config.brand;
  const brand = typeof brandRaw === 'object' ? (brandRaw?.name || '') : (brandRaw || '');
  const { footer } = config;
  return (
    <footer className="jb-footer">
      <div className="jb-footer__wrap">
        <div className="jb-footer__top">
          <div>
            <div className="jb-footer__brand">{brand}</div>
            <p className="jb-footer__tag">{footer.tagline}</p>
            <div className="jb-footer__email">
              <input type="email" placeholder={footer.emailPlaceholder} />
              <button type="button">ASSINAR</button>
            </div>
            <div className="jb-footer__socials">
              <a href={footer.socials.instagram} title="Instagram"><IconInstagram /></a>
              <a href={footer.socials.facebook}  title="Facebook"><IconFacebook /></a>
              <a href={footer.socials.twitter}   title="Twitter"><IconTwitter /></a>
              <a href={footer.socials.tiktok}    title="TikTok"><IconTikTok /></a>
            </div>
          </div>
          <ul className="jb-footer__links">
            {footer.links.map((link) => (
              <li key={link.label}><a href={link.href}>{link.label}</a></li>
            ))}
          </ul>
        </div>
        <div className="jb-footer__bottom">
          <div className="jb-footer__copy">{footer.copyright}</div>
        </div>
      </div>
    </footer>
  );
}